import { Chart } from "chart.js/auto"; // TODO: only import the parts we need

import * as api from "../scripts/api";
import { bindData } from "../scripts/binding";
import { loading } from "../scripts/loading-spinner";
import accuracyStats from "./accuracy-stats.json";

Chart.defaults.backgroundColor = '#9BD0F5';
Chart.defaults.borderColor = '#444';

const last100 = [] as {
  foundCount: number;
  moveCount: number;
  rate: number;
}[];

const current = {
  accuracy: 0,
  rate: 0,
  totalFound: 0,
  totalMoves: 0,
  overallAccuracy: 0,
};

const chartData = {
  accuracy100: [] as number[],
  accuracy1000: [] as number[],
  rate100: [] as number[],
};

const accuracyChart = new Chart(
  document.getElementById("accuracy-trend") as HTMLCanvasElement,
  {
    type: "line",
    data: {
      labels: [] as number[],
      datasets: [{
        label: "Current Accuracy",
        data: chartData.accuracy100,
        borderWidth: 1,
        yAxisID: "y",
      }, {
        label: "Overall Accuracy",
        data: chartData.accuracy1000,
        borderWidth: 1,
        yAxisID: "y",
      }, {
        label: "Time Per Piece",
        data: chartData.rate100,
        borderWidth: 1,
        yAxisID: "y1",
      }],
    },
    options: {
      elements: {
        point: {
          pointStyle: false,
        },
      },
      aspectRatio: 1.5,
      scales: {
        x: {
          title: {
            display: true,
            text: "Positions Ago",
            color: "#b5b5b5",
          },
          reverse: true,
          type: "linear",
          suggestedMax: 100,
          suggestedMin: 0,
        },
        y: {
          title: {
            display: true,
            text: "Accuracy (%)",
            color: "#b5b5b5",
          },
          beginAtZero: true,
          max: 100,
          min: 0,
        },
        y1: {
          title: {
            display: true,
            text: "Time per piece (s)",
            color: "#b5b5b5",
          },
          beginAtZero: true,
          position: "right",
          grid: {
            drawOnChartArea: false,
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: "#b5b5b5",
            textAlign: "left",
          },
        },
      },
    },
  },
);

// const ctx = document.getElementById('overall-performance')! as HTMLCanvasElement;
const ctx = document.querySelector<HTMLCanvasElement>("#overall-performance")!;

// Replace these values with your own data
const userPosition = {} as { x: number; y: number };
const allUsers = accuracyStats
  .filter((s) => s.t > 10)
  .map((s) => ({
    x: s.a * 100,
    y: s.tpp,
  }));

const scatterChart = new Chart(ctx, {
  type: "scatter",
  data: {
    datasets: [
      {
        label: "You",
        data: [userPosition],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        pointRadius: 5,
        pointHoverRadius: 8,
      },
      {
        label: "All Players",
        data: allUsers,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        pointRadius: 3,
        pointHoverRadius: 6,
      },
    ],
  },
  options: {
    aspectRatio: 1,
    scales: {
      x: {
        title: {
          display: true,
          text: "🎯 Accuracy (%)",
          color: "#b5b5b5",
        },
        max: 100,
      },
      y: {
        title: {
          display: true,
          text: "⏱ Median Time Per Piece (s)",
          color: "#b5b5b5",
        },
        min: 0,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          color: "#b5b5b5",
        },
      },
    },
  },
});

export const loadAccuracy = async () => {
  bindData({ "accuracy100": loading.outerHTML });
  const trend = await api.getTrend();
  const latestData = trend[0];

  // Fill the last100 array with the latest data
  const recentTrend = trend.slice(0, 100);
  recentTrend.forEach((t) => {
    last100.push({
      foundCount: t.found_count,
      moveCount: t.move_count,
      rate: t.rate,
    });
  });

  // Iterate through the trend in reverse order
  trend.reverse().forEach((t, i) => {
    addChartData(t.accuracy1000, t.accuracy100, t.rate100);
  });
  accuracyChart.update();

  if (latestData) {
    current.totalFound = latestData.found1000;
    current.totalMoves = latestData.move1000;
    current.overallAccuracy = latestData.accuracy1000;
    current.rate = latestData.rate100;
    current.accuracy = latestData.accuracy100;
    userPosition.x = latestData.accuracy100 * 100;
    userPosition.y = latestData.rate100;
  }
  scatterChart.update();
  renderAccuracy();
};

export const updateAccuracy = (
  foundCount: number,
  moveCount: number,
  pieceCount: number,
  elapsedTime: number,
) => {
  // Accumulate the last 100 moves
  const rate = elapsedTime / (pieceCount + foundCount);
  last100.unshift({ foundCount, moveCount, rate });
  if (last100.length > 100) last100.pop();

  // Update our current stats
  current.totalFound += foundCount;
  current.totalMoves += moveCount;
  current.overallAccuracy = current.totalFound / current.totalMoves;
  current.rate = calcMedianRate();
  current.accuracy = calcCurrentAccuracy();
  renderAccuracy();

  addChartData(current.overallAccuracy, current.accuracy, current.rate);
  accuracyChart.update();
  return {
    ...current,
    count: last100.length,
  };
};

function addChartData(
  overallAccuracy: number,
  currentAccuracy: number,
  rate: number,
) {
  chartData.accuracy1000.unshift(overallAccuracy * 100);
  chartData.accuracy100.unshift(currentAccuracy * 100);
  chartData.rate100.unshift(rate);

  if (chartData.accuracy1000.length > 1000) {
    chartData.accuracy100.pop();
    chartData.accuracy1000.pop();
    chartData.rate100.pop();
  } else {
    accuracyChart.data.labels?.push(chartData.accuracy100.length);
  }
}

async function renderAccuracy() {
  const currentAccuracy = current.accuracy;
  const overallAccuracy = current.overallAccuracy;
  const tpp = current.rate;

  // If accuracy is trending up, show a green up arrow
  // If accuracy is trending down, show a red down arrow
  const trendIconClass = currentAccuracy > overallAccuracy
    ? "bi-arrow-up-right has-text-success"
    : currentAccuracy < overallAccuracy
    ? "bi-arrow-down-right has-text-danger"
    : "";

  bindData({
    "accuracy100": currentAccuracy
      ? currentAccuracy.toLocaleString(undefined, { style: "percent" })
      : "-",
    "accuracy1000": overallAccuracy
      ? overallAccuracy.toLocaleString(undefined, { style: "percent" })
      : "-",
    "tpp": tpp
      ? tpp.toLocaleString(undefined, { style: "decimal" }) + " s"
      : "-",
    "accuracy-trend-icon": `<i class="bi ${trendIconClass}"></i>`,
  });
}

const calcCurrentAccuracy = () =>
  last100.reduce((acc, cur) => acc + cur.foundCount, 0) /
  last100.reduce((acc, cur) => acc + cur.moveCount, 0);

const calcMedianRate = () => {
  const rates = last100.map((x) => x.rate).sort((a, b) => a - b); // Add a compare function to sort numerically
  const midIndex = Math.floor(rates.length / 2);

  if (rates.length % 2 === 0) {
    // If the number of elements is even, return the average of the two middle elements
    return (rates[midIndex - 1] + rates[midIndex]) / 2;
  } else {
    // If the number of elements is odd, return the middle element
    return rates[midIndex];
  }
};
